import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, HeartHandshakeIcon, TimerIcon, TagIcon, ShieldCheckIcon, TrendingUpIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const advantageIcons = [<DatabaseIcon className="w-6 h-6" data-id="element-38" />, <HeartHandshakeIcon className="w-6 h-6" data-id="element-39" />, <TimerIcon className="w-6 h-6" data-id="element-40" />, <TagIcon className="w-6 h-6" data-id="element-41" />, <ShieldCheckIcon className="w-6 h-6" data-id="element-42" />, <TrendingUpIcon className="w-6 h-6" data-id="element-43" />];
const advantageStyles = [{
  iconBg: 'from-blue-100 to-blue-50',
  iconColor: 'text-blue-600',
  border: 'border-l-blue-400'
}, {
  iconBg: 'from-rose-100 to-rose-50',
  iconColor: 'text-rose-600',
  border: 'border-l-rose-400'
}, {
  iconBg: 'from-amber-100 to-amber-50',
  iconColor: 'text-amber-600',
  border: 'border-l-amber-400'
}, {
  iconBg: 'from-emerald-100 to-emerald-50',
  iconColor: 'text-emerald-600',
  border: 'border-l-emerald-400'
}, {
  iconBg: 'from-violet-100 to-violet-50',
  iconColor: 'text-violet-600',
  border: 'border-l-violet-400'
}, {
  iconBg: 'from-primary-100 to-primary-50',
  iconColor: 'text-primary-600',
  border: 'border-l-primary-400'
}];
export function AdvantagesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-navy-50/50 overflow-hidden" data-id="element-44">
      <div className="absolute inset-0 pointer-events-none" data-id="element-45">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-100/20 blur-3xl hidden sm:block" data-id="element-46" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] rounded-full bg-cyan-100/15 blur-3xl hidden sm:block" data-id="element-47" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-48">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-49">
          {/* Left: text */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-50">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-51">
              {t.advantages.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-52">
              {t.advantages.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-53">
              {t.advantages.subtitle}
            </p>

            <div className="mt-10 space-y-4" data-id="element-54">
              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="relative p-6 rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-navy-800 text-white overflow-hidden" data-id="element-55">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/5 blur-2xl" data-id="element-56" />
                <div className="absolute inset-0 opacity-[0.06]" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
                backgroundSize: '24px 24px'
              }} data-id="element-57" />
                <div className="relative" data-id="element-58">
                  <div className="text-4xl font-extrabold tracking-tight" data-id="element-59">
                    98%
                  </div>
                  <div className="mt-1 text-primary-200 text-sm font-medium" data-id="element-60">
                    {t.advantages.stat1_label}
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="flex gap-4" data-id="element-61">
                <div className="flex-1 p-5 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-62">
                  <div className="flex items-center gap-2 mb-1" data-id="element-63">
                    <StarIcon className="w-4 h-4 text-amber-500" data-id="element-64" />
                    <span className="text-2xl font-extrabold text-navy-900" data-id="element-65">
                      {t.advantages.stat2_val}
                    </span>
                  </div>
                  <div className="text-xs text-navy-500 font-medium" data-id="element-66">
                    {t.advantages.stat2_label}
                  </div>
                </div>
                <div className="flex-1 p-5 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-67">
                  <div className="flex items-center gap-2 mb-1" data-id="element-68">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-69" />
                    <span className="text-2xl font-extrabold text-navy-900" data-id="element-70">
                      {t.advantages.stat3_val}
                    </span>
                  </div>
                  <div className="text-xs text-navy-500 font-medium" data-id="element-71">
                    {t.advantages.stat3_label}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: advantages list */}
          <div className="space-y-4" data-id="element-72">
            {t.advantages.items.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 + index * 0.1
          }} className={`flex gap-4 p-5 rounded-xl bg-white border border-navy-100 shadow-sm hover:shadow-md transition-shadow duration-300 border-l-4 ${advantageStyles[index].border}`} data-id="element-73">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${advantageStyles[index].iconBg} ${advantageStyles[index].iconColor} flex items-center justify-center`} data-id="element-74">
                  {advantageIcons[index]}
                </div>
                <div data-id="element-75">
                  <h3 className="font-bold text-navy-900" data-id="element-76">{item.title}</h3>
                  <p className="mt-1 text-sm text-navy-500 leading-relaxed" data-id="element-77">
                    {item.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}